import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { warehouseAPI } from "../../services/api";
import { Printer, BarChart3, Package, Warehouse, FileSpreadsheet, History } from "lucide-react";
import { gregorianToJalaliStr } from "../../utils/PersianDatePicker";
import PersianDatePicker from "../../utils/PersianDatePicker";
import SearchableSelect from "../../components/SearchableSelect";
import jalaliMoment from "jalali-moment";

const fmt=(n:any,d=3)=>new Intl.NumberFormat("en-US",{minimumFractionDigits:0,maximumFractionDigits:d}).format(Number(n)||0);
const getNowJalali=()=>{const now=new Date();return jalaliMoment(now).locale("fa").format("jYYYY/jMM/jDD")+" — "+now.toTimeString().slice(0,5);};

function printStockAll(products:any[], warehouses:any[], warehouseName="همه گدام‌ها") {
  const now = getNowJalali();
  const rows = products.map((p,i)=>{
    const total=Number(p.total_stock);
    const color=total<0?"#dc2626":total===0?"#64748b":"#059669";
    const byWh = (p.by_warehouse||[]).map((bw:any)=>`<span style="font-size:10px;background:${Number(bw.quantity)<0?"#fee2e2":Number(bw.quantity)===0?"#f1f5f9":"#d1fae5"};color:${Number(bw.quantity)<0?"#dc2626":"#059669"};padding:2px 7px;border-radius:4px;margin:2px">${bw.warehouse_name}: ${fmt(bw.quantity)}</span>`).join(" ");
    const lowStock = p.min_stock>0&&total<=p.min_stock;
    return `<tr style="background:${total<0?"#fff1f1":i%2===0?"#fff":"#f8faff"}">
      <td style="text-align:center;color:#94a3b8;font-size:11px">${i+1}</td>
      <td><strong style="color:#10b981;font-family:monospace">${p.code}</strong>${p.barcode?`<br/><span style="font-size:10px;color:#94a3b8">${p.barcode}</span>`:""}</td>
      <td><strong>${p.name}</strong>${lowStock?` <span style="font-size:9px;background:#fef3c7;color:#d97706;padding:1px 5px;border-radius:3px">⚠ کم‌موجودی</span>`:""}</td>
      <td style="text-align:center;color:#64748b">${p.category_name||"—"}</td>
      <td style="text-align:center;color:#64748b">${p.unit_name||"—"}</td>
      <td style="text-align:center;font-weight:900;font-size:15px;color:${color}">${fmt(total)}${total<0?' <span style="font-size:10px">⚠</span>':""}</td>
      <td>${byWh||"—"}</td>
    </tr>`;
  }).join("");

  const neg  = products.filter(p=>Number(p.total_stock)<0).length;
  const zero = products.filter(p=>Number(p.total_stock)===0).length;
  const pos  = products.filter(p=>Number(p.total_stock)>0).length;

  const html=`<!DOCTYPE html><html dir="rtl" lang="fa">
  <head><meta charset="UTF-8"/><title>گزارش موجودی گدام</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;700;900&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Vazirmatn',sans-serif;background:#fff;color:#1e293b;padding:24px;font-size:12px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #10b981;padding-bottom:16px;margin-bottom:18px}
    .title{font-size:20px;font-weight:900;color:#10b981}
    .stats{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:18px}
    .stat{border-radius:10px;padding:12px 16px;text-align:center}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#10b981;color:white;padding:9px 10px;font-weight:700;font-size:11px;text-align:center}
    td{padding:8px 10px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
    tfoot td{background:#f0fdf4;font-weight:900;border-top:2px solid #10b981}
    .footer{margin-top:20px;text-align:center;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:12px;display:flex;justify-content:space-between}
    @media print{body{padding:10px}@page{margin:0.8cm;size:A4 landscape}}
  </style></head><body>
  <div class="header">
    <div><div class="title">📦 گزارش موجودی گدام</div><div style="font-size:12px;color:#64748b;margin-top:3px">گدام: ${warehouseName}</div></div>
    <div style="text-align:left;color:#64748b;font-size:11px;line-height:1.9"><div>🖨️ تاریخ چاپ: <strong style="color:#10b981">${now}</strong></div><div>تعداد اجناس: <strong>${products.length}</strong></div></div>
  </div>
  <div class="stats">
    <div class="stat" style="background:#f0fdf4;border:1px solid #bbf7d0"><div style="font-size:22px;font-weight:900;color:#059669">${pos}</div><div style="font-size:11px;color:#64748b;margin-top:2px">موجود</div></div>
    <div class="stat" style="background:#f8faff;border:1px solid #e2e8f0"><div style="font-size:22px;font-weight:900;color:#64748b">${zero}</div><div style="font-size:11px;color:#64748b;margin-top:2px">صفر</div></div>
    <div class="stat" style="background:#fff1f1;border:1px solid #fecaca"><div style="font-size:22px;font-weight:900;color:#dc2626">${neg}</div><div style="font-size:11px;color:#64748b;margin-top:2px">موجودی منفی</div></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>کد</th><th>نام جنس</th><th>دسته‌بندی</th><th>واحد</th><th>موجودی کل</th><th>به تفکیک گدام</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer"><span>Hamidi Group Finance — سیستم مدیریت گدام</span><span>🖨️ ${now}</span></div>
  <script>window.onload=()=>window.print();</script>
  </body></html>`;
  const w=window.open("","_blank");if(w){w.document.write(html);w.document.close();}
}

function printWarehouseStock(stock:any[], warehouse:any) {
  const now=getNowJalali();
  const rows=stock.map((s,i)=>{
    const qty=Number(s.quantity);
    const color=qty<0?"#dc2626":qty===0?"#64748b":"#059669";
    const low=s.min_stock>0&&qty<=s.min_stock;
    return `<tr style="background:${qty<0?"#fff1f1":i%2===0?"#fff":"#f8faff"}">
      <td style="text-align:center;color:#94a3b8">${i+1}</td>
      <td><strong style="color:#8b5cf6;font-family:monospace">${s.product_code}</strong></td>
      <td><strong>${s.product_name}</strong>${low?` <span style="font-size:9px;background:#fef3c7;color:#d97706;padding:1px 5px;border-radius:3px">⚠ کم‌موجودی</span>`:""}</td>
      <td style="text-align:center;color:#64748b">${s.category_name||"—"}</td>
      <td style="text-align:center;color:#64748b">${s.unit_name||"—"}</td>
      <td style="text-align:center;font-weight:900;font-size:14px;color:${color}">${fmt(qty)}${qty<0?' ⚠':""}</td>
      <td style="text-align:center;color:#94a3b8;font-size:10px">${s.min_stock>0?fmt(s.min_stock,0):"—"}</td>
    </tr>`;
  }).join("");

  const html=`<!DOCTYPE html><html dir="rtl" lang="fa">
  <head><meta charset="UTF-8"/><title>موجودی گدام — ${warehouse.name}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;700;900&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Vazirmatn',sans-serif;background:#fff;color:#1e293b;padding:24px;font-size:12px}
    .header{display:flex;justify-content:space-between;border-bottom:3px solid #8b5cf6;padding-bottom:16px;margin-bottom:18px}
    .title{font-size:20px;font-weight:900;color:#8b5cf6}
    table{width:100%;border-collapse:collapse}
    th{background:#8b5cf6;color:white;padding:9px 10px;font-weight:700;font-size:11px}
    td{padding:8px 10px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
    .footer{margin-top:20px;display:flex;justify-content:space-between;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:12px}
    @media print{body{padding:10px}@page{margin:0.8cm;size:A4}}
  </style></head><body>
  <div class="header">
    <div>
      <div class="title">🏭 موجودی گدام: ${warehouse.name}</div>
      <div style="font-size:11px;color:#64748b;margin-top:4px;line-height:1.8">
        کد: ${warehouse.code}
        ${warehouse.manager_name?`• مسئول: ${warehouse.manager_name}`:""}
        ${warehouse.address?`• آدرس: ${warehouse.address}`:""}
      </div>
    </div>
    <div style="text-align:left;font-size:11px;color:#64748b;line-height:1.9">
      <div>🖨️ تاریخ چاپ: <strong style="color:#8b5cf6">${now}</strong></div>
      <div>تعداد اجناس: <strong>${stock.length}</strong></div>
    </div>
  </div>
  <table>
    <thead><tr><th>#</th><th>کد</th><th>نام جنس</th><th>دسته‌بندی</th><th>واحد</th><th>موجودی</th><th>حداقل</th></tr></thead>
    <tbody>${rows||'<tr><td colspan="7" style="text-align:center;padding:20px;color:#94a3b8">موجودی ثبت نشده</td></tr>'}</tbody>
  </table>
  <div class="footer"><span>Hamidi Group Finance</span><span>🖨️ ${now}</span></div>
  <script>window.onload=()=>window.print();</script>
  </body></html>`;
  const w=window.open("","_blank");if(w){w.document.write(html);w.document.close();}
}

// ============================================================
// 🆕 چاپ گزارش ورودی/خروجی اجناس
// ============================================================
const TYPE_LABELS: any = {
  purchase: "🛒 خرید",
  sale: "💰 فروش",
  transfer_out: "↗️ انتقال (خروج)",
  transfer_in: "↘️ انتقال (ورود)",
  distribution: "📦 تخلیه از بار",
  manual_in: "➕ ثبت دستی (ورود)",
  manual_out: "➖ ثبت دستی (خروج)",
};
const TYPE_COLORS: any = {
  purchase: "#10b981",
  sale: "#ef4444",
  transfer_out: "#f59e0b",
  transfer_in: "#3b82f6",
  distribution: "#8b5cf6",
  manual_in: "#06b6d4",
  manual_out: "#dc2626",
};

function printLedger(data: any, ledgerWhName: string, fromDate: string, toDate: string) {
  const now = getNowJalali();
  const product = data.product || {};
  const stocks = data.stocks || [];
  const rows = data.rows || [];
  const summary = data.summary || {};
  
  const trRows = rows.map((r: any, i: number) => {
    const dateGreg = r.tx_date ? jalaliMoment(r.tx_date, "YYYY-MM-DD").locale("en").format("D MMM YYYY") : "—";
    const dateJal = r.tx_date ? gregorianToJalaliStr(r.tx_date) : "—";
    const color = TYPE_COLORS[r.src_type] || "#64748b";
    const label = TYPE_LABELS[r.src_type] || r.src_type;
    const qtyIn = Number(r.qty_in) || 0;
    const qtyOut = Number(r.qty_out) || 0;
    const balance = Number(r.running_balance) || 0;
    const balColor = balance < 0 ? "#dc2626" : balance === 0 ? "#64748b" : "#059669";
    return `<tr style="background:${i%2===0?"#fff":"#fafbfc"}">
      <td style="text-align:center;color:#94a3b8;font-size:10px">${i+1}</td>
      <td style="text-align:center;font-size:10px"><div style="font-weight:700">${dateJal}</div><div style="color:#94a3b8;font-size:9px">${dateGreg}</div></td>
      <td><span style="background:${color}22;color:${color};padding:3px 8px;border-radius:4px;font-size:10px;font-weight:700;white-space:nowrap">${label}</span></td>
      <td style="text-align:center;font-family:monospace;font-size:10px;color:#475569">${r.doc_serial || "—"}</td>
      <td style="font-size:11px"><div style="font-weight:600">${r.warehouse_name || "—"}</div></td>
      <td style="text-align:center;font-family:monospace;font-size:10px;color:${r.truck_plate?'#8b5cf6':'#cbd5e1'};font-weight:${r.truck_plate?'700':'400'}">${r.truck_plate || "—"}</td>
      <td style="text-align:center;font-family:monospace;font-size:10px;color:${r.waybill_number?'#0ea5e9':'#cbd5e1'};font-weight:${r.waybill_number?'700':'400'}">${r.waybill_number || "—"}</td>
      <td style="text-align:center;font-size:13px;font-weight:800;color:#10b981">${qtyIn>0?"+"+fmt(qtyIn):"—"}</td>
      <td style="text-align:center;font-size:13px;font-weight:800;color:#ef4444">${qtyOut>0?"−"+fmt(qtyOut):"—"}</td>
      <td style="text-align:center;font-size:13px;font-weight:900;color:${balColor}">${fmt(balance)}</td>
      <td style="font-size:10px"><div style="font-weight:600;color:#1e293b">${r.party_name || "—"}</div>${r.party_role?`<div style="color:#94a3b8;font-size:9px">${r.party_role}</div>`:""}</td>
      <td style="font-size:9px;color:#64748b;line-height:1.4;max-width:180px">${r.doc_notes || ""}</td>
    </tr>`;
  }).join("");
  
  const stockRows = stocks.map((s: any) => 
    `<span style="display:inline-block;background:${Number(s.quantity)<0?"#fee2e2":Number(s.quantity)===0?"#f1f5f9":"#d1fae5"};color:${Number(s.quantity)<0?"#dc2626":Number(s.quantity)===0?"#64748b":"#059669"};padding:4px 10px;border-radius:5px;margin:2px;font-size:11px;font-weight:700">${s.warehouse_name}: ${fmt(s.quantity)}</span>`
  ).join(" ");
  
  const totalCurrentStock = stocks.reduce((sum: number, s: any) => sum + Number(s.quantity || 0), 0);
  
  const dateRangeText = fromDate || toDate 
    ? `بازه: ${fromDate ? gregorianToJalaliStr(fromDate) : "ابتدا"} تا ${toDate ? gregorianToJalaliStr(toDate) : "امروز"}`
    : "تمامی تراکنش‌ها";
  
  const html = `<!DOCTYPE html><html dir="rtl" lang="fa">
  <head><meta charset="UTF-8"/><title>گزارش ورودی/خروجی اجناس - ${product.name}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800;900&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Vazirmatn',sans-serif;background:#fff;color:#1e293b;padding:20px;font-size:11px;direction:rtl}
    .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0ea5e9;padding-bottom:14px;margin-bottom:14px}
    .title{font-size:20px;font-weight:900;color:#0ea5e9;margin-bottom:3px}
    .subtitle{font-size:11px;color:#64748b;font-weight:600}
    .product-info{background:linear-gradient(135deg,#f0f9ff,#e0f2fe);border:1px solid #0284c7;border-right:4px solid #0ea5e9;border-radius:6px;padding:10px 14px;margin-bottom:14px}
    .product-name{font-size:16px;font-weight:900;color:#0c4a6e;margin-bottom:3px}
    .product-meta{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:6px;font-size:10px}
    .product-meta strong{display:block;color:#075985;font-size:9px;margin-bottom:1px}
    .product-meta span{color:#0c4a6e;font-weight:700}
    .stocks-row{background:#fafbfc;border:1px solid #e5e5e5;border-radius:6px;padding:10px 14px;margin-bottom:14px}
    .stocks-label{font-size:10px;font-weight:700;color:#64748b;margin-bottom:6px;letter-spacing:0.3px}
    .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:14px}
    .summary-card{border-radius:8px;padding:10px 12px;text-align:center;border:1.5px solid}
    .summary-card .lbl{font-size:9px;font-weight:700;margin-bottom:3px;letter-spacing:0.3px}
    .summary-card .val{font-size:16px;font-weight:900}
    table{width:100%;border-collapse:collapse;font-size:10px;border:1px solid #e5e5e5}
    th{background:#0ea5e9;color:white;padding:7px 5px;font-weight:700;font-size:10px;text-align:center;border-left:1px solid #38bdf8}
    th:last-child{border-left:none}
    td{padding:6px 5px;border-bottom:1px solid #f1f5f9;border-left:1px solid #f8fafc;vertical-align:middle}
    td:last-child{border-left:none}
    .footer{margin-top:14px;border-top:1px dashed #cbd5e1;padding-top:8px;display:flex;justify-content:space-between;color:#94a3b8;font-size:10px}
    .signatures{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px}
    .sig{border:1px dashed #94a3b8;border-radius:6px;padding:18px 8px 8px;text-align:center;position:relative;min-height:60px;background:#fafafa}
    .sig-label{position:absolute;top:-7px;right:50%;transform:translateX(50%);background:white;padding:0 8px;font-size:9px;font-weight:700;color:#475569;white-space:nowrap}
    .sys-footer{position:fixed;bottom:5mm;left:0;right:0;text-align:center;font-size:7px;color:#0ea5e9;font-weight:700;letter-spacing:0.4px;white-space:nowrap}
    @media print{body{padding:8mm}@page{margin:0;size:A4 landscape}}
  </style></head><body>
  
  <div class="header">
    <div>
      <div class="title">📜 گزارش ورودی/خروجی اجناس</div>
      <div class="subtitle">Product Ledger Report</div>
    </div>
    <div style="text-align:left;font-size:10px;color:#64748b">
      <div style="font-weight:700">تاریخ چاپ:</div>
      <div>${now}</div>
    </div>
  </div>
  
  <div class="product-info">
    <div class="product-name">${product.name || "—"}</div>
    <div class="product-meta">
      <div><strong>کد محصول</strong><span style="font-family:monospace">${product.code || "—"}</span></div>
      <div><strong>دسته‌بندی</strong><span>${product.category_name || "—"}</span></div>
      <div><strong>واحد</strong><span>${product.unit_name || product.unit_symbol || "—"}</span></div>
      <div><strong>گدام</strong><span>${ledgerWhName || "همه گدام‌ها"}</span></div>
    </div>
    <div style="margin-top:6px;font-size:10px;color:#075985"><strong>بازه گزارش:</strong> ${dateRangeText}</div>
  </div>
  
  <div class="stocks-row">
    <div class="stocks-label">💼 موجودی فعلی به تفکیک گدام:</div>
    ${stockRows || '<span style="color:#94a3b8;font-size:10px">— موجودی ثبت نشده —</span>'}
    <div style="margin-top:6px;padding-top:6px;border-top:1px solid #e5e5e5;font-size:11px"><strong>مجموع کل:</strong> <span style="font-weight:900;color:${totalCurrentStock<0?"#dc2626":"#0c4a6e"}">${fmt(totalCurrentStock)}</span></div>
  </div>
  
  <div class="summary">
    <div class="summary-card" style="border-color:#10b981;background:#f0fdf4">
      <div class="lbl" style="color:#065f46">جمع ورودی‌ها</div>
      <div class="val" style="color:#10b981">+${fmt(summary.total_in)}</div>
    </div>
    <div class="summary-card" style="border-color:#ef4444;background:#fef2f2">
      <div class="lbl" style="color:#991b1b">جمع خروجی‌ها</div>
      <div class="val" style="color:#ef4444">−${fmt(summary.total_out)}</div>
    </div>
    <div class="summary-card" style="border-color:#0ea5e9;background:#f0f9ff">
      <div class="lbl" style="color:#075985">خالص گردش</div>
      <div class="val" style="color:${(summary.net||0)<0?'#dc2626':'#0ea5e9'}">${(summary.net||0)>=0?'+':''}${fmt(summary.net)}</div>
    </div>
    <div class="summary-card" style="border-color:#8b5cf6;background:#faf5ff">
      <div class="lbl" style="color:#581c87">تعداد تراکنش</div>
      <div class="val" style="color:#8b5cf6">${summary.count || 0}</div>
    </div>
  </div>
  
  <table>
    <thead>
      <tr>
        <th style="width:25px">#</th>
        <th style="width:70px">تاریخ</th>
        <th style="width:95px">نوع تراکنش</th>
        <th style="width:75px">شماره سند</th>
        <th style="width:85px">گدام</th>
        <th style="width:70px">🚚 موتر</th>
        <th style="width:75px">📄 بارنامه</th>
        <th style="width:65px">ورود</th>
        <th style="width:65px">خروج</th>
        <th style="width:70px">مانده</th>
        <th style="width:100px">طرف</th>
        <th>توضیحات</th>
      </tr>
    </thead>
    <tbody>${trRows || '<tr><td colspan="12" style="text-align:center;padding:20px;color:#94a3b8">— هیچ تراکنشی یافت نشد —</td></tr>'}</tbody>
  </table>
  
  <div class="signatures">
    <div class="sig"><div class="sig-label">امضای انباردار</div></div>
    <div class="sig"><div class="sig-label">امضای مسئول مالی</div></div>
  </div>
  
  <div class="footer">
    <span>تاریخ چاپ: ${now}</span>
    <span>تعداد ردیف: ${rows.length}</span>
  </div>
  
  <div class="sys-footer">نرم افزار حسابداری گلبرگ - ورژن 2026.3.01.04 — mustafasaberi3344@gmail.com</div>
  
  <script>window.onload=()=>window.print();</script>
  </body></html>`;
  
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

// ============================================================
// 🆕 خروجی اکسل (CSV با BOM برای پشتیبانی فارسی)
// ============================================================
function exportLedgerExcel(data: any, ledgerWhName: string) {
  const product = data.product || {};
  const rows = data.rows || [];
  const summary = data.summary || {};
  
  const headers = ["ردیف", "تاریخ شمسی", "تاریخ میلادی", "نوع تراکنش", "شماره سند", "گدام", "شماره موتر", "شماره بارنامه", "ورود", "خروج", "مانده", "طرف معامله", "نقش", "توضیحات"];
  
  const csvRows = rows.map((r: any, i: number) => {
    const dateGreg = r.tx_date ? jalaliMoment(r.tx_date, "YYYY-MM-DD").locale("en").format("YYYY-MM-DD") : "";
    const dateJal = r.tx_date ? gregorianToJalaliStr(r.tx_date) : "";
    const label = TYPE_LABELS[r.src_type] || r.src_type;
    return [
      i + 1,
      dateJal,
      dateGreg,
      label.replace(/[🛒💰↗️↘️📦➕➖]/g, "").trim(),
      r.doc_serial || "",
      r.warehouse_name || "",
      r.truck_plate || "",
      r.waybill_number || "",
      r.qty_in > 0 ? r.qty_in : "",
      r.qty_out > 0 ? r.qty_out : "",
      r.running_balance,
      r.party_name || "",
      r.party_role || "",
      (r.doc_notes || "").replace(/[\r\n,;]/g, " "),
    ];
  });
  
  // اضافه کردن خلاصه در انتها
  csvRows.push([]);
  csvRows.push(["", "", "", "", "", "", "", "", "جمع کل ورودی‌ها:", summary.total_in || 0]);
  csvRows.push(["", "", "", "", "", "", "", "", "جمع کل خروجی‌ها:", summary.total_out || 0]);
  csvRows.push(["", "", "", "", "", "", "", "", "خالص گردش:", summary.net || 0]);
  
  const escape = (v: any) => {
    const s = String(v ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  
  const csv = [
    `# گزارش ورودی/خروجی اجناس`,
    `# محصول: ${product.name || ""}  - کد: ${product.code || ""}  - گدام: ${ledgerWhName || "همه"}`,
    "",
    headers.map(escape).join(","),
    ...csvRows.map((row: any[]) => row.map(escape).join(",")),
  ].join("\n");
  
  // BOM برای UTF-8 (فارسی در اکسل درست نمایش داده شود)
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `product_ledger_${product.code || product.id}_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [report, setReport]       = useState<"all"|"warehouse"|"ledger">("all");
  const [stockAll, setStockAll]   = useState<any[]>([]);
  const [whStock, setWhStock]     = useState<any[]>([]);
  const [warehouses, setWarehouses]= useState<any[]>([]);
  const [selWh, setSelWh]         = useState("");
  const [selWhObj, setSelWhObj]   = useState<any>(null);
  const [cats, setCats]           = useState<any[]>([]);
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading]     = useState(false);
  
  // 🆕 state های گزارش ورودی/خروجی اجناس
  const [products, setProducts]       = useState<any[]>([]);
  const [ledgerProductId, setLedgerProductId] = useState("");
  const [ledgerWhId, setLedgerWhId]   = useState("");
  const [ledgerFrom, setLedgerFrom]   = useState("");
  const [ledgerTo, setLedgerTo]       = useState("");
  const [ledgerData, setLedgerData]   = useState<any>(null);
  
  // 🆕 فیلترهای داخل جدول گزارش
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerTruckFilter, setLedgerTruckFilter] = useState("");
  const [ledgerWaybillFilter, setLedgerWaybillFilter] = useState("");

  const inp: React.CSSProperties = { padding:"9px 12px", background:dark?"rgba(255,255,255,0.05)":"#f8faff", border:`1.5px solid ${dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`, borderRadius:8, color:T.text, fontSize:13, fontFamily:"'Vazirmatn','Segoe UI',sans-serif", direction:"rtl" as const, outline:"none" };
  const lbl: React.CSSProperties = { fontSize:11, fontWeight:700, color:T.subtext, display:"block", marginBottom:4 };
  const card: React.CSSProperties = { borderRadius:14, padding:20, background:dark?"rgba(255,255,255,0.03)":"#fff", border:`1px solid ${T.cardBorder}`, marginBottom:14 };

  useEffect(()=>{
    warehouseAPI.warehousesList().then(r=>{if(r.success)setWarehouses(r.warehouses);});
    warehouseAPI.productCatsList().then(r=>{if(r.success)setCats(r.categories);});
    warehouseAPI.productsList({status:"active"}).then(r=>{if(r.success)setProducts(r.products);});
  },[]);

  const loadAllStock=async()=>{
    setLoading(true);
    const r=await warehouseAPI.reportStockAll({category_id:filterCat});
    if(r.success)setStockAll(r.products);
    setLoading(false);
  };
  const loadWhStock=async()=>{
    if(!selWh)return;
    setLoading(true);
    const r=await warehouseAPI.reportWarehouseStock(selWh);
    if(r.success){setWhStock(r.stock);setSelWhObj(r.warehouse);}
    setLoading(false);
  };
  
  // 🆕 لود گزارش ورودی/خروجی اجناس
  const loadLedger = async () => {
    if (!ledgerProductId) return;
    setLoading(true);
    const r = await warehouseAPI.productLedger({
      product_id: parseInt(ledgerProductId),
      warehouse_id: ledgerWhId ? parseInt(ledgerWhId) : 0,
      from: ledgerFrom || "",
      to: ledgerTo || "",
    });
    if (r.success) setLedgerData(r);
    setLoading(false);
  };

  useEffect(()=>{if(report==="all")loadAllStock();},[report,filterCat]);
  useEffect(()=>{if(report==="warehouse"&&selWh)loadWhStock();},[report,selWh]);
  useEffect(()=>{if(report==="ledger"&&ledgerProductId)loadLedger();},[report,ledgerProductId,ledgerWhId,ledgerFrom,ledgerTo]);

  const fmt3=(n:any)=>fmt(n,3);
  const stockColor=(n:number)=>n<0?"#ef4444":n===0?"#64748b":"#10b981";

  return (
    <div dir="rtl">
      {/* انتخاب نوع گزارش */}
      <div style={{display:"flex",gap:10,marginBottom:20}}>
        {[
          {id:"all",     label:"📦 موجودی کل همه اجناس", color:"#10b981"},
          {id:"warehouse",label:"🏭 موجودی یک گدام",      color:"#8b5cf6"},
          {id:"ledger",  label:"📜 ورودی/خروجی اجناس",     color:"#0ea5e9"},
        ].map(r=>(
          <button key={r.id} onClick={()=>setReport(r.id as any)} style={{flex:1,padding:"12px 20px",borderRadius:11,border:`1.5px solid ${report===r.id?r.color:T.cardBorder}`,background:report===r.id?`${r.color}14`:"transparent",color:report===r.id?r.color:T.subtext,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",transition:"all 0.2s"}}>
            {r.label}
          </button>
        ))}
      </div>

      {/* فیلترها */}
      <div style={{...card,marginBottom:16}}>
        <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
          {report==="all"&&(
            <div>
              <label style={lbl}>فیلتر دسته‌بندی</label>
              <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{...inp,background:dark?"#1e293b":"#f8faff"}}>
                <option value="">همه دسته‌ها</option>
                {cats.map(c=><option key={c.id} value={c.id}>{c.parent_id?"↳ ":""}{c.name}</option>)}
              </select>
            </div>
          )}
          {report==="warehouse"&&(
            <div>
              <label style={lbl}>انتخاب گدام *</label>
              <select value={selWh} onChange={e=>setSelWh(e.target.value)} style={{...inp,background:dark?"#1e293b":"#f8faff"}}>
                <option value="">— انتخاب گدام</option>
                {warehouses.map(w=><option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
              </select>
            </div>
          )}
          <button
            onClick={()=>{
              if(report==="all") printStockAll(stockAll,warehouses);
              else if(report==="warehouse"&&selWhObj) printWarehouseStock(whStock,selWhObj);
            }}
            style={{display:"flex",alignItems:"center",gap:7,padding:"9px 20px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#10b981,#059669)",color:"white",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700}}
          >
            <Printer size={14}/> چاپ گزارش
          </button>
        </div>
      </div>

      {/* ══ گزارش کل موجودی ══ */}
      {report==="all"&&(
        loading
          ?<div style={{textAlign:"center",padding:60,color:T.subtext}}><div style={{width:36,height:36,border:"3px solid rgba(16,185,129,0.2)",borderTop:"3px solid #10b981",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/></div>
          :<div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
              {[
                {label:"موجود",     val:stockAll.filter(p=>Number(p.total_stock)>0).length,  color:"#10b981"},
                {label:"صفر",       val:stockAll.filter(p=>Number(p.total_stock)===0).length, color:"#64748b"},
                {label:"موجودی منفی",val:stockAll.filter(p=>Number(p.total_stock)<0).length,  color:"#ef4444"},
              ].map((s,i)=>(
                <div key={i} style={{borderRadius:12,padding:"14px 18px",background:dark?"rgba(255,255,255,0.03)":"#fff",border:`1px solid ${s.color}30`,borderTop:`3px solid ${s.color}`}}>
                  <div style={{fontSize:11,color:T.subtext,marginBottom:6}}>{s.label}</div>
                  <div style={{fontSize:24,fontWeight:900,color:s.color}}>{s.val}</div>
                </div>
              ))}
            </div>
            <div style={{borderRadius:14,overflow:"hidden",border:`1px solid ${T.cardBorder}`,background:dark?"rgba(255,255,255,0.02)":"#fff"}}>
              <div style={{display:"grid",gridTemplateColumns:"70px 1fr 110px 90px 100px 180px",gap:8,padding:"11px 16px",background:dark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)",borderBottom:`1px solid ${T.cardBorder}`,fontSize:11,fontWeight:700,color:T.subtext}}>
                <span>کد</span><span>نام جنس</span><span>دسته‌بندی</span><span>واحد</span><span>موجودی کل</span><span>به تفکیک گدام</span>
              </div>
              {stockAll.map(p=>{
                const total=Number(p.total_stock);
                return (
                  <div key={p.id} style={{display:"grid",gridTemplateColumns:"70px 1fr 110px 90px 100px 180px",gap:8,padding:"10px 16px",borderBottom:`1px solid ${T.cardBorder}`,alignItems:"center",background:total<0?(dark?"rgba(239,68,68,0.06)":"rgba(239,68,68,0.03)"):undefined}}>
                    <span style={{fontSize:10,fontFamily:"monospace",color:"#10b981",fontWeight:700}}>{p.code}</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:T.text}}>{p.name}</div>
                      {p.min_stock>0&&total<=p.min_stock&&<span style={{fontSize:9,background:"rgba(245,158,11,0.15)",color:"#f59e0b",padding:"1px 5px",borderRadius:3,fontWeight:700}}>⚠ کم‌موجودی</span>}
                    </div>
                    <span style={{fontSize:11,color:T.subtext}}>{p.category_name||"—"}</span>
                    <span style={{fontSize:11,color:T.subtext}}>{p.unit_symbol||p.unit_name||"—"}</span>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:stockColor(total)}}>{fmt3(total)}</div>
                      {total<0&&<div style={{fontSize:9,color:"#ef4444"}}>موجودی منفی</div>}
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {(p.by_warehouse||[]).map((bw:any,i:number)=>(
                        <span key={i} style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:Number(bw.quantity)<0?"rgba(239,68,68,0.1)":Number(bw.quantity)===0?"rgba(100,116,139,0.1)":"rgba(16,185,129,0.1)",color:Number(bw.quantity)<0?"#ef4444":Number(bw.quantity)===0?"#64748b":"#10b981",fontWeight:600}}>
                          {bw.warehouse_name}: {fmt3(bw.quantity)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
      )}

      {/* ══ گزارش یک گدام ══ */}
      {report==="warehouse"&&(
        !selWh
          ?<div style={{textAlign:"center",padding:60,color:T.subtext}}><div style={{fontSize:40,marginBottom:10}}>🏭</div>یک گدام انتخاب کنید</div>
          :loading
          ?<div style={{textAlign:"center",padding:60,color:T.subtext}}><div style={{width:36,height:36,border:"3px solid rgba(139,92,246,0.2)",borderTop:"3px solid #8b5cf6",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/></div>
          :<div>
            {selWhObj&&(
              <div style={{...card,borderRight:"4px solid #8b5cf6",marginBottom:16}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,fontSize:12}}>
                  <div><div style={{color:T.subtext,fontSize:10,marginBottom:2}}>نام گدام</div><div style={{fontWeight:700,color:T.text}}>{selWhObj.name}</div></div>
                  <div><div style={{color:T.subtext,fontSize:10,marginBottom:2}}>کد</div><div style={{fontWeight:700,color:"#8b5cf6"}}>{selWhObj.code}</div></div>
                  <div><div style={{color:T.subtext,fontSize:10,marginBottom:2}}>مسئول</div><div style={{fontWeight:700,color:T.text}}>{selWhObj.manager_name||"—"}</div></div>
                  {selWhObj.address&&<div style={{gridColumn:"1/-1"}}><div style={{color:T.subtext,fontSize:10,marginBottom:2}}>آدرس</div><div style={{color:T.text}}>{selWhObj.address}</div></div>}
                </div>
              </div>
            )}
            <div style={{borderRadius:14,overflow:"hidden",border:`1px solid ${T.cardBorder}`,background:dark?"rgba(255,255,255,0.02)":"#fff"}}>
              <div style={{display:"grid",gridTemplateColumns:"70px 1fr 110px 90px 100px 80px",gap:8,padding:"11px 16px",background:dark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)",borderBottom:`1px solid ${T.cardBorder}`,fontSize:11,fontWeight:700,color:T.subtext}}>
                <span>کد</span><span>نام جنس</span><span>دسته‌بندی</span><span>واحد</span><span>موجودی</span><span>حداقل</span>
              </div>
              {whStock.length===0
                ?<div style={{textAlign:"center",padding:40,color:T.subtext}}>موجودی ثبت نشده</div>
                :whStock.map(s=>{
                  const qty=Number(s.quantity);
                  const low=s.min_stock>0&&qty<=s.min_stock;
                  return (
                    <div key={s.id} style={{display:"grid",gridTemplateColumns:"70px 1fr 110px 90px 100px 80px",gap:8,padding:"10px 16px",borderBottom:`1px solid ${T.cardBorder}`,alignItems:"center",background:qty<0?(dark?"rgba(239,68,68,0.06)":"rgba(239,68,68,0.03)"):undefined}}>
                      <span style={{fontSize:10,fontFamily:"monospace",color:"#8b5cf6",fontWeight:700}}>{s.product_code}</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:T.text}}>{s.product_name}</div>
                        {low&&<span style={{fontSize:9,background:"rgba(245,158,11,0.15)",color:"#f59e0b",padding:"1px 5px",borderRadius:3,fontWeight:700}}>⚠ کم‌موجودی</span>}
                      </div>
                      <span style={{fontSize:11,color:T.subtext}}>{s.category_name||"—"}</span>
                      <span style={{fontSize:11,color:T.subtext}}>{s.unit_symbol||s.unit_name||"—"}</span>
                      <div style={{fontSize:14,fontWeight:800,color:stockColor(qty)}}>{fmt3(qty)}{qty<0&&<span style={{fontSize:10}}> ⚠</span>}</div>
                      <span style={{fontSize:11,color:T.subtext}}>{s.min_stock>0?fmt(s.min_stock,0):"—"}</span>
                    </div>
                  );
                })
              }
            </div>
          </div>
      )}

      {/* ══ 🆕 گزارش ورودی/خروجی اجناس (Product Ledger) ══ */}
      {report==="ledger"&&(
        <div>
          {/* فیلترها */}
          <div style={{...card,marginBottom:16,borderRight:"4px solid #0ea5e9"}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr auto auto",gap:10,alignItems:"end"}}>
              <div>
                <label style={lbl}>انتخاب کالا *</label>
                <SearchableSelect
                  options={products.map((p:any)=>({
                    value: String(p.id),
                    label: `📦 ${p.name}${p.code?` — ${p.code}`:''}`
                  }))}
                  value={ledgerProductId}
                  onChange={v=>setLedgerProductId(v)}
                  placeholder="🔍 جستجو و انتخاب کالا..."
                />
              </div>
              <div>
                <label style={lbl}>گدام (اختیاری)</label>
                <SearchableSelect
                  options={[{value:"",label:"همه گدام‌ها"},...warehouses.map((w:any)=>({value:String(w.id),label:`🏭 ${w.name}`}))]}
                  value={ledgerWhId}
                  onChange={v=>setLedgerWhId(v)}
                  placeholder="همه گدام‌ها"
                />
              </div>
              <div>
                <PersianDatePicker label="از تاریخ" value={ledgerFrom} onChange={v=>setLedgerFrom(v)}/>
              </div>
              <div>
                <PersianDatePicker label="تا تاریخ" value={ledgerTo} onChange={v=>setLedgerTo(v)}/>
              </div>
              <button
                onClick={()=>{
                  if(!ledgerData||!ledgerProductId){return;}
                  const whName = ledgerWhId ? (warehouses.find((w:any)=>String(w.id)===ledgerWhId)?.name || "") : "همه گدام‌ها";
                  printLedger(ledgerData, whName, ledgerFrom, ledgerTo);
                }}
                disabled={!ledgerData||!ledgerProductId}
                style={{display:"flex",alignItems:"center",gap:6,padding:"9px 16px",borderRadius:9,border:"none",background:!ledgerData||!ledgerProductId?"rgba(0,0,0,0.1)":"linear-gradient(135deg,#0ea5e9,#0284c7)",color:"white",cursor:!ledgerData||!ledgerProductId?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700,opacity:!ledgerData||!ledgerProductId?0.5:1,whiteSpace:"nowrap"}}
              >
                <Printer size={14}/> چاپ
              </button>
              <button
                onClick={()=>{
                  if(!ledgerData||!ledgerProductId){return;}
                  const whName = ledgerWhId ? (warehouses.find((w:any)=>String(w.id)===ledgerWhId)?.name || "") : "همه گدام‌ها";
                  exportLedgerExcel(ledgerData, whName);
                }}
                disabled={!ledgerData||!ledgerProductId}
                style={{display:"flex",alignItems:"center",gap:6,padding:"9px 16px",borderRadius:9,border:"none",background:!ledgerData||!ledgerProductId?"rgba(0,0,0,0.1)":"linear-gradient(135deg,#10b981,#059669)",color:"white",cursor:!ledgerData||!ledgerProductId?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700,opacity:!ledgerData||!ledgerProductId?0.5:1,whiteSpace:"nowrap"}}
              >
                <FileSpreadsheet size={14}/> اکسل
              </button>
            </div>
          </div>

          {!ledgerProductId
            ?<div style={{textAlign:"center",padding:60,color:T.subtext}}>
              <div style={{fontSize:50,marginBottom:14}}>📜</div>
              <div style={{fontSize:14,fontWeight:700,marginBottom:6}}>برای دیدن گزارش، یک کالا انتخاب کنید</div>
              <div style={{fontSize:11,opacity:0.7}}>گزارش شامل تمام خریدها، فروش‌ها، انتقال‌ها و توزیع‌های ثبت‌شده می‌شود</div>
            </div>
            :loading
            ?<div style={{textAlign:"center",padding:60,color:T.subtext}}><div style={{width:36,height:36,border:"3px solid rgba(14,165,233,0.2)",borderTop:"3px solid #0ea5e9",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/></div>
            :ledgerData&&(
              <div>
                {/* کارت اطلاعات محصول */}
                <div style={{...card,borderRight:"4px solid #0ea5e9",marginBottom:14,background:dark?"rgba(14,165,233,0.05)":"#f0f9ff"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:14}}>
                    <div style={{flex:1,minWidth:240}}>
                      <div style={{fontSize:18,fontWeight:900,color:dark?"#7dd3fc":"#0c4a6e",marginBottom:4}}>📦 {ledgerData.product?.name}</div>
                      <div style={{display:"flex",gap:14,fontSize:11,color:T.subtext,flexWrap:"wrap"}}>
                        <span>کد: <strong style={{fontFamily:"monospace",color:dark?"#7dd3fc":"#0284c7"}}>{ledgerData.product?.code}</strong></span>
                        <span>دسته: <strong style={{color:T.text}}>{ledgerData.product?.category_name||"—"}</strong></span>
                        <span>واحد: <strong style={{color:T.text}}>{ledgerData.product?.unit_name||ledgerData.product?.unit_symbol||"—"}</strong></span>
                      </div>
                    </div>
                  </div>
                  
                  {/* موجودی به تفکیک گدام */}
                  {ledgerData.stocks&&ledgerData.stocks.length>0&&(
                    <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.cardBorder}`}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.subtext,marginBottom:6}}>💼 موجودی فعلی به تفکیک گدام:</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {ledgerData.stocks.map((s:any)=>(
                          <span key={s.id} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:700,background:Number(s.quantity)<0?"rgba(239,68,68,0.15)":Number(s.quantity)===0?"rgba(0,0,0,0.05)":"rgba(16,185,129,0.15)",color:Number(s.quantity)<0?"#ef4444":Number(s.quantity)===0?T.subtext:"#10b981"}}>
                            {s.warehouse_name}: {fmt3(Number(s.quantity))}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* کارت‌های خلاصه */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:14}}>
                  <div style={{padding:"14px 16px",borderRadius:12,background:dark?"rgba(16,185,129,0.08)":"#f0fdf4",border:"1.5px solid rgba(16,185,129,0.3)",textAlign:"center"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#059669",marginBottom:4}}>📥 جمع ورودی‌ها</div>
                    <div style={{fontSize:20,fontWeight:900,color:"#10b981"}}>+{fmt3(ledgerData.summary?.total_in)}</div>
                  </div>
                  <div style={{padding:"14px 16px",borderRadius:12,background:dark?"rgba(239,68,68,0.08)":"#fef2f2",border:"1.5px solid rgba(239,68,68,0.3)",textAlign:"center"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#dc2626",marginBottom:4}}>📤 جمع خروجی‌ها</div>
                    <div style={{fontSize:20,fontWeight:900,color:"#ef4444"}}>−{fmt3(ledgerData.summary?.total_out)}</div>
                  </div>
                  <div style={{padding:"14px 16px",borderRadius:12,background:dark?"rgba(14,165,233,0.08)":"#f0f9ff",border:"1.5px solid rgba(14,165,233,0.3)",textAlign:"center"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#0284c7",marginBottom:4}}>⚖️ خالص گردش</div>
                    <div style={{fontSize:20,fontWeight:900,color:(ledgerData.summary?.net||0)<0?"#dc2626":"#0ea5e9"}}>{(ledgerData.summary?.net||0)>=0?"+":""}{fmt3(ledgerData.summary?.net)}</div>
                  </div>
                  <div style={{padding:"14px 16px",borderRadius:12,background:dark?"rgba(139,92,246,0.08)":"#faf5ff",border:"1.5px solid rgba(139,92,246,0.3)",textAlign:"center"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#7c3aed",marginBottom:4}}>📋 تعداد تراکنش</div>
                    <div style={{fontSize:20,fontWeight:900,color:"#8b5cf6"}}>{ledgerData.summary?.count||0}</div>
                  </div>
                </div>

                {/* 🆕 فیلترهای داخل جدول گزارش — کارت لوکس */}
                <div style={{
                  marginBottom: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: dark
                    ? "linear-gradient(135deg,rgba(14,165,233,0.08),rgba(2,132,199,0.04))"
                    : "linear-gradient(135deg,#f0f9ff,#e0f2fe)",
                  border: `1px solid ${dark ? "rgba(14,165,233,0.25)" : "#bae6fd"}`,
                  boxShadow: dark ? "none" : "0 4px 20px rgba(14,165,233,0.08)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: "linear-gradient(135deg,#0ea5e9,#0284c7)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, boxShadow: "0 3px 8px rgba(14,165,233,0.3)",
                    }}>🔍</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: dark ? "#7dd3fc" : "#0c4a6e" }}>
                      جستجو و فیلتر در گزارش
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ ...lbl, color: dark ? "#7dd3fc" : "#075985" }}>🔎 جستجوی همگانی</label>
                      <input
                        value={ledgerSearch}
                        onChange={e => setLedgerSearch(e.target.value)}
                        placeholder="نوع تراکنش، گدام، طرف، شرح..."
                        style={{ ...inp, width: "100%" }}
                      />
                    </div>
                    <div>
                      <label style={{ ...lbl, color: dark ? "#a78bfa" : "#6d28d9" }}>🚚 شماره موتر</label>
                      <input
                        value={ledgerTruckFilter}
                        onChange={e => setLedgerTruckFilter(e.target.value)}
                        placeholder="فیلتر شماره موتر..."
                        style={{ ...inp, width: "100%", direction: "ltr" as const, fontFamily: "monospace" }}
                      />
                    </div>
                    <div>
                      <label style={{ ...lbl, color: dark ? "#7dd3fc" : "#0284c7" }}>📄 شماره بارنامه</label>
                      <input
                        value={ledgerWaybillFilter}
                        onChange={e => setLedgerWaybillFilter(e.target.value)}
                        placeholder="فیلتر شماره بارنامه..."
                        style={{ ...inp, width: "100%", direction: "ltr" as const, fontFamily: "monospace" }}
                      />
                    </div>
                  </div>
                </div>

                {/* جدول تراکنش‌ها — لوکس با اسکرول */}
                {(() => {
                  // فیلتر کلاینت‌ساید
                  const filteredRows = (ledgerData.rows || []).filter((r: any) => {
                    if (ledgerSearch) {
                      const q = ledgerSearch.toLowerCase();
                      const typeLabel = TYPE_LABELS[r.src_type] || r.src_type;
                      const match = (typeLabel || "").toLowerCase().includes(q)
                                 || (r.warehouse_name || "").toLowerCase().includes(q)
                                 || (r.party_name || "").toLowerCase().includes(q)
                                 || (r.doc_notes || "").toLowerCase().includes(q)
                                 || (r.doc_serial || "").toLowerCase().includes(q);
                      if (!match) return false;
                    }
                    if (ledgerTruckFilter) {
                      if (!(r.truck_plate || "").toLowerCase().includes(ledgerTruckFilter.toLowerCase())) return false;
                    }
                    if (ledgerWaybillFilter) {
                      if (!(r.waybill_number || "").toLowerCase().includes(ledgerWaybillFilter.toLowerCase())) return false;
                    }
                    return true;
                  });
                  // 10 ردیف آخر همیشه نمایش بده — بقیه با اسکرول
                  const totalRows = filteredRows.length;

                  return (
                    <div style={{
                      borderRadius: 16,
                      overflow: "hidden",
                      border: `1px solid ${dark ? T.cardBorder : "#e0f2fe"}`,
                      background: dark ? "rgba(255,255,255,0.02)" : "#fff",
                      boxShadow: dark ? "none" : "0 8px 32px rgba(14,165,233,0.06), 0 2px 6px rgba(0,0,0,0.03)",
                    }}>
                      {/* هدر اطلاعاتی */}
                      <div style={{
                        padding: "12px 18px",
                        background: dark
                          ? "linear-gradient(135deg,rgba(14,165,233,0.15),rgba(2,132,199,0.08))"
                          : "linear-gradient(135deg,#0ea5e9,#0284c7)",
                        borderBottom: `1px solid ${dark ? T.cardBorder : "transparent"}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9,
                            background: dark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.25)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 16, backdropFilter: "blur(10px)",
                          }}>📊</div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>
                              جدول ورودی و خروجی
                            </div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", marginTop: 2 }}>
                              {totalRows} تراکنش یافت شد
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: 11, fontWeight: 700, color: "#fff",
                          padding: "5px 12px", borderRadius: 8,
                          background: "rgba(255,255,255,0.2)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255,255,255,0.3)",
                        }}>
                          📜 اسکرول برای نمایش بیشتر
                        </div>
                      </div>

                      {/* هدر ستون‌ها — sticky */}
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "35px 85px 115px 90px 1fr 90px 100px 80px 80px 90px 1.1fr 1.3fr",
                        gap: 6, padding: "11px 14px",
                        background: dark ? "rgba(255,255,255,0.04)" : "linear-gradient(180deg,#f8fafc,#f1f5f9)",
                        borderBottom: `2px solid ${dark ? "rgba(14,165,233,0.2)" : "#bae6fd"}`,
                        fontSize: 10, fontWeight: 800, color: dark ? T.subtext : "#475569",
                        position: "sticky" as const, top: 0, zIndex: 2,
                      }}>
                        <span>#</span>
                        <span>📅 تاریخ</span>
                        <span>نوع تراکنش</span>
                        <span>🔖 سند</span>
                        <span>🏭 گدام</span>
                        <span>🚚 موتر</span>
                        <span>📄 بارنامه</span>
                        <span style={{ textAlign: "center" }}>📥 ورود</span>
                        <span style={{ textAlign: "center" }}>📤 خروج</span>
                        <span style={{ textAlign: "center" }}>⚖️ مانده</span>
                        <span>طرف معامله</span>
                        <span>📝 توضیحات</span>
                      </div>

                      {/* بدنه قابل اسکرول — به اندازه 10 ردیف ارتفاع */}
                      <div style={{
                        maxHeight: 560,
                        overflowY: "auto" as const,
                        scrollbarWidth: "thin" as const,
                      }}>
                        {filteredRows.length === 0
                          ? <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
                              <div style={{ fontSize: 40, marginBottom: 10 }}>📭</div>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>هیچ تراکنشی مطابق با فیلترها یافت نشد</div>
                            </div>
                          : filteredRows.map((r: any, i: number) => {
                              const qtyIn = Number(r.qty_in) || 0;
                              const qtyOut = Number(r.qty_out) || 0;
                              const balance = Number(r.running_balance) || 0;
                              const balColor = balance < 0 ? "#ef4444" : balance === 0 ? T.subtext : "#10b981";
                              const typeColor = TYPE_COLORS[r.src_type] || T.subtext;
                              const typeLabel = TYPE_LABELS[r.src_type] || r.src_type;
                              const dateJal = r.tx_date ? gregorianToJalaliStr(r.tx_date) : "—";
                              const dateGreg = r.tx_date ? jalaliMoment(r.tx_date, "YYYY-MM-DD").locale("en").format("D MMM YYYY") : "";
                              return (
                                <div key={`${r.src_type}-${r.doc_id}-${i}`} style={{
                                  display: "grid",
                                  gridTemplateColumns: "35px 85px 115px 90px 1fr 90px 100px 80px 80px 90px 1.1fr 1.3fr",
                                  gap: 6, padding: "12px 14px",
                                  borderBottom: `1px solid ${dark ? T.cardBorder : "#f1f5f9"}`,
                                  alignItems: "center",
                                  background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "rgba(14,165,233,0.02)"),
                                  transition: "background 0.15s",
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.background = dark ? "rgba(14,165,233,0.08)" : "rgba(14,165,233,0.06)";
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.background = i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "rgba(14,165,233,0.02)");
                                }}
                                >
                                  <span style={{ fontSize: 10, color: T.subtext, fontWeight: 700 }}>{i + 1}</span>
                                  <div style={{ fontSize: 10 }}>
                                    <div style={{ fontWeight: 700, color: T.text }}>{dateJal}</div>
                                    <div style={{ color: T.subtext, fontSize: 9 }}>{dateGreg}</div>
                                  </div>
                                  <span><span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: `${typeColor}22`, color: typeColor, whiteSpace: "nowrap" }}>{typeLabel}</span></span>
                                  <span style={{ fontSize: 10, fontFamily: "monospace", color: T.subtext }}>{r.doc_serial || "—"}</span>
                                  <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{r.warehouse_name || "—"}</span>
                                  <span style={{ fontSize: 10, fontFamily: "monospace", fontWeight: r.truck_plate ? 700 : 400, color: r.truck_plate ? "#8b5cf6" : T.subtext, direction: "ltr" as const }}>{r.truck_plate || "—"}</span>
                                  <span style={{ fontSize: 10, fontFamily: "monospace", fontWeight: r.waybill_number ? 700 : 400, color: r.waybill_number ? "#0ea5e9" : T.subtext, direction: "ltr" as const }}>{r.waybill_number || "—"}</span>
                                  <span style={{ fontSize: 13, fontWeight: 800, color: "#10b981", textAlign: "center" }}>{qtyIn > 0 ? "+" + fmt3(qtyIn) : "—"}</span>
                                  <span style={{ fontSize: 13, fontWeight: 800, color: "#ef4444", textAlign: "center" }}>{qtyOut > 0 ? "−" + fmt3(qtyOut) : "—"}</span>
                                  <span style={{ fontSize: 13, fontWeight: 900, color: balColor, textAlign: "center" }}>{fmt3(balance)}</span>
                                  <div style={{ fontSize: 11 }}>
                                    <div style={{ fontWeight: 600, color: T.text }}>{r.party_name || "—"}</div>
                                    {r.party_role && <div style={{ fontSize: 9, color: T.subtext }}>{r.party_role}</div>}
                                  </div>
                                  <div style={{ fontSize: 10, color: T.subtext, lineHeight: 1.5, whiteSpace: "normal", wordBreak: "break-word" }}>{r.doc_notes || "—"}</div>
                                </div>
                              );
                            })
                        }
                      </div>
                    </div>
                  );
                })()}
              </div>
            )
          }
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}